import type { HttpErrorResponse } from '@angular/common/http';

import type { CorrelationId, PickActions } from '@recruitee/ngrx';
import { createEntityCollectionStore, pipeState } from '@recruitee/ngrx';
import type { Stage, StageGroup } from '@recruitee/stages-types';
import type { StageForm } from '@recruitee/template-builder';

export interface MovePayload {
  pipelineTemplateId: number;
  stageId: number;
  group: StageGroup;
  placeAfterStageId?: number;
}
export interface UpdatePositionsPayload {
  pipelineTemplateId: number;
  stagesIds: number[];
}

const idSelector = ({ id }: { id: number }) => id;
const { actions, reducer, selectors, namespace } = createEntityCollectionStore<Stage>()(
  'PipelineTemplateStage',
  {
    idSelector,
    customActions: adapter => ({
      createOnePessimistic: (
        state,
        payload: { pipelineTemplateId: number; form: StageForm } & Required<CorrelationId>,
      ) => adapter.markAsPending(state, { scope: null, entities: [payload.correlationId] }),

      updateOnePessimistic: (state, payload: { pipelineTemplateId: number; form: StageForm }) =>
        adapter.markAsPending(state, { scope: null, entities: [payload.form.id!] }),

      removeOnePessimistic: (
        state,
        payload: { pipelineTemplateId: number; stageId: number; destinationStageId?: number },
      ) => adapter.markAsPending(state, { scope: null, entities: [payload.stageId] }),

      move: (state, payload: MovePayload) =>
        adapter.markAsPending(state, { scope: null, entities: [payload.stageId] }),
      moveSuccess: (state, payload: { data: Stage }) => adapter.upsertOne(state, payload.data),
      moveFailed: (state, payload: { error: HttpErrorResponse; data: MovePayload }) =>
        adapter.setError(state, payload.error, { scope: null, entities: [payload.data.stageId] }),

      updatePositions: (state, payload: UpdatePositionsPayload) =>
        adapter.markAsPending(state, { scope: null, entities: payload.stagesIds }),
      updatePositionsSuccess: (state, payload: { data: Stage[]; pipelineTemplateId: number }) =>
        pipeState(
          state,
          next => adapter.updateMany(next, payload.data),
          next =>
            adapter.markAsLoaded(next, { scope: null, entities: payload.data.map(idSelector) }),
        ),
      updatePositionsFailed: (
        state,
        payload: { error: HttpErrorResponse; data: UpdatePositionsPayload },
      ) =>
        adapter.setError(state, payload.error, { scope: null, entities: payload.data.stagesIds }),
    }),
  },
);

const PipelineTemplateStageActions: PickActions<
  typeof actions,
  | 'createOnePessimistic'
  | 'updateOnePessimistic'
  | 'removeOnePessimistic'
  | 'updatePositions'
  | 'move'
> = actions;

export {
  PipelineTemplateStageActions,
  reducer as PipelineTemplateStageReducer,
  selectors as PipelineTemplateStageSelectors,
  namespace as PipelineTemplateStageNamespace,
};
